import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Bell, BellOff, CheckCircle2 } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../ui/button";
import { Dialog } from "../ui/Dialog";
import { errorMessage } from "./format";

function applicationKey(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const binary = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function deviceId(): string {
  const key = "slacking-push-device";
  const current = localStorage.getItem(key);
  if (current) return current;
  const created = crypto.randomUUID();
  localStorage.setItem(key, created);
  return created;
}

export function NotificationSettingsDialog({ onClose }: { onClose: () => void }) {
  const configuration = useQuery(api.notifications.configuration, {});
  const status = useQuery(api.notifications.status, {});
  const register = useMutation(api.notifications.register);
  const unregister = useMutation(api.notifications.unregister);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!supported || configuration?.publicKey === undefined) throw new Error("Push notifications are not available on this device yet.");
      const permission = await Notification.requestPermission();
      if (permission !== "granted") throw new Error("Notifications were not allowed. You can change that in this browser's site settings.");
      const serviceWorker = await navigator.serviceWorker.ready;
      const subscription = await serviceWorker.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationKey(configuration.publicKey) });
      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error("This browser returned an incomplete notification subscription.");
      await register({ deviceId: deviceId(), endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth, platform: /iPhone|iPad|iPod/u.test(navigator.userAgent) ? "ios" : "web" });
    } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      const serviceWorker = await navigator.serviceWorker.ready;
      const subscription = await serviceWorker.pushManager.getSubscription();
      if (subscription !== null) {
        await unregister({ endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }
    } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog title="Notifications" onClose={onClose}>
      <div className="stack notification-settings">
        <div className="settings-hero">
          <span className="settings-hero__icon">{status?.enabled === true ? <CheckCircle2 size={22} /> : <Bell size={22} />}</span>
          <div><strong>{status?.enabled === true ? "Notifications are on" : "Never miss a teammate"}</strong><p>Yodl sends a private, generic alert. Message text and patient information never appear in the notification.</p></div>
        </div>
        {!supported ? <p className="form-notice">This browser does not support push notifications. Use an installed Safari, Chrome, Edge, or the Yodl app.</p> : null}
        {configuration?.available === false ? <p className="form-notice">The notification service is built, but the production signing keys have not been installed yet.</p> : null}
        <div className="dialog__actions dialog__actions--spread">
          <span className="nav-hint">{status?.devices ? `${status.devices} enabled ${status.devices === 1 ? "device" : "devices"}` : "No enabled devices"}</span>
          {status?.enabled === true ? <Button variant="ghost" disabled={busy} onClick={() => { void disable(); }}><BellOff size={14} /> Turn off here</Button> : <Button variant="primary" disabled={busy || !supported || configuration?.available !== true} onClick={() => { void enable(); }}><Bell size={14} /> Enable notifications</Button>}
        </div>
        {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
      </div>
    </Dialog>
  );
}
